/**
 * Component identity across a param-only route change (#2822).
 *
 * Vue Router reuses a mounted component when only the params change, so a page
 * whose entire surface is scoped to one record keeps its instance — and every
 * in-flight flow, busy flag, dialog and success panel it owns — when the record
 * underneath it changes. Guarding that per handler was attempted on the two
 * officer COT pages during #2819 and produced four successive defects, each
 * introduced by the fix for the one before, because a guard has to enumerate
 * everything the transition invalidates and the list is open-ended.
 *
 * Marking the route removes the premise instead: a change of identity mounts a
 * fresh component, so there is nothing left to carry over and nothing to reset.
 * Use it for a page that is wholly about one `:id` and holds state a different
 * `:id` must not inherit — not as a blanket setting, since it costs the reuse
 * that makes a param change cheap.
 *
 * The key is applied by each shell/layout that owns a `<router-view>`: the app
 * root, club/guest shells, and dashboard workspace. The route test pins the
 * flagged set so a route flagged somewhere else fails rather than silently
 * keeping its instance.
 */
import type { RouteLocationNormalizedLoaded } from "vue-router";

declare module "vue-router" {
  interface RouteMeta {
    /**
     * Never reuse this route's component when only its params change. Set it on
     * a page whose state is scoped to one record, where inheriting anything
     * from the previously displayed one is a defect rather than a saving.
     */
    remountOnParamChange?: boolean;
    /** Apply the identity key at the app shell for a top-level route. */
    remountAtAppShell?: boolean;
  }
}

/** The subset of a resolved route this module reads. */
type RouteIdentity = Pick<RouteLocationNormalizedLoaded, "path" | "meta">;

/**
 * The `:key` to render a route's component under, or `undefined` to keep Vue
 * Router's default reuse.
 *
 * Keyed on `path`, not `fullPath`: the params identify the record, whereas a
 * query or hash change (a filter, a tab, an anchor) is the same record being
 * looked at differently and must not throw the page away mid-flow.
 */
export function routeInstanceKey(route: RouteIdentity): string | undefined {
  return route.meta.remountOnParamChange ? route.path : undefined;
}

/**
 * Key a top-level page at the app root without remounting shared club or
 * dashboard layouts when only their child route changes.
 */
export function appShellInstanceKey(route: RouteIdentity): string | undefined {
  return route.meta.remountAtAppShell ? route.path : undefined;
}
